import { FeatureService } from '@core/feature-preview/helpers/feature.service';
import { computed, inject, signal } from '@angular/core';
import { FeaturePreview } from '@core/feature-preview/types/feature-preview';
import { FeatureKey } from '@core/feature-preview/types/feature-key';

export type FeatureFilter = 'all' | 'enabled' | 'disabled';

export interface EditableFeature {
  feature: FeaturePreview;
  enabled: boolean;
}

export class FeatureCenterService {
  /** INJECTIONS **/
  /****/
  private readonly featureService = inject(FeatureService);

  /****/
  private _features = signal<EditableFeature[]>([]);

  /****/
  public readonly features = computed(() => {
    return this._features()
      .filter(feat => {
        if (this.filter === 'enabled') return feat.enabled;
        else if (this.filter === 'disabled') return !feat.enabled;
        else return true;
      })
      .filter(feat => {
        return feat.feature.name
          .toLocaleLowerCase()
          .includes(this.search.toLocaleLowerCase());
      });
  });

  /****/
  private readonly _changeMap = signal<Record<string, 'disabled' | 'enabled'>>(
    {}
  );

  /****/
  public readonly changes = this._changeMap.asReadonly();

  /****/
  public dirty = computed(() => Object.values(this._changeMap()).length !== 0);

  /****/
  private readonly _search = signal<string>('');

  public set search(searchTerm: string) {
    this._search.set(searchTerm);
  }

  public get search() {
    return this._search();
  }

  /****/
  private readonly _filter = signal<FeatureFilter>('all');

  public set filter(filter: FeatureFilter) {
    this._filter.set(filter);
  }

  public get filter() {
    return this._filter();
  }

  public get totalFeatureCount() {
    return this._features().length;
  }

  public get enabledFeatureCount() {
    return this._features().filter(feat => feat.enabled).length;
  }

  constructor() {
    const features = this.featureService.getFeaturesAvailableForCurrentUser();
    this._features.set(this.mapFeaturesToEditable(features));
  }

  public toggleFeature(key: FeatureKey) {
    let newEnabled: boolean | undefined = undefined;
    const updated = this._features().map(feature => {
      if (feature.feature.key !== key) return feature;

      newEnabled = !feature.enabled;
      return { ...feature, enabled: !feature.enabled };
    });

    if (newEnabled === undefined) return; // feature with provided key was not found

    const updateAction = newEnabled ? 'enabled' : 'disabled';
    const recordedAction = this._changeMap()[key];
    if (!recordedAction) {
      this._changeMap.update(cur => {
        return { ...cur, [key]: updateAction };
      });
    } else if (recordedAction !== updateAction) {
      this._changeMap.update(cur => {
        const { [key]: _, ...temp } = cur;
        return temp;
      });
    }

    this._features.set(updated);
  }

  public resetChanges() {
    this._changeMap.set({});
  }

  private mapFeaturesToEditable(features: FeaturePreview[]): EditableFeature[] {
    return features.map(feature => {
      const enabled = this.featureService.isFeatureEnabled(feature.key);
      return { feature, enabled };
    });
  }
}
